import { Button, InputAdornment, Paper, TextField } from '@mui/material';
import { blueGrey } from '@mui/material/colors';
import Stack from '@mui/material/Stack';
import { useGridApiRef } from '@mui/x-data-grid';
import type { InvoiceOverview, InvoicesOverviewsSummary } from '@weft/api-definition';
import { VyIcon } from '@weft/icons';
import { INVOICE_REVIEW_STATUSES, type InvoiceBillingStatus, type InvoiceDate } from '@weft/types';
import { extract, isEmptyArray,
  isNotNullOrUndefined,
  validateNotEmptyArray, 
  validateNotNullOrUndefined } from '@weft/utils';
import { useConnectedIntegrations } from 'apps/app/src/hooks/requests/use-integrations';
import { useSyncNetSuiteInvoice } from 'apps/app/src/hooks/requests/use-net-suite';
import { useSyncToStripe } from 'apps/app/src/hooks/requests/use-sync-stripe';
import { useActivate } from 'apps/app/src/hooks/use-activate';
import { runMutation } from 'apps/app/src/utils/vy-mutation';
import React, { useCallback, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { CalendarDropdown } from '../../../../components/design/calendar-dropdown/CalendarDropdown';
import { SearchIcon } from '../../../../components/icons';
import { USE_INVOICES_OVERVIEWS_QUERY_KEY } from '../../../../hooks/requests/use-invoices-overviews/use-invoices-overviews-consts';
import { useUpdateInvoicesBillingStatuses } from '../../../../hooks/requests/use-update-invoices-billing-statuses';
import { useUpdateInvoicesReviewStatuses } from '../../../../hooks/requests/use-update-invoices-review-statuses';
import { useDebounce } from '../../../../hooks/use-debounce';
import { useDialog } from '../../../../hooks/use-dialog';
import { getCurrentDate } from '../../../../utils/date';
import { MONTHS_LABELS } from '../../../customers/CustomerOverviewPage/customer-view-page-components/BillingScheduleChart/billing-schedule-chart-consts';
import { LightInvoicesPdfDownloader } from '../../LightInvoiceViewPage/light-invoices-pdf-downloader/LightInvoicesPdfDownloader';
import { useDatesParam } from '../invoices-page-hooks/use-months-param';
import { useQueryInputParams } from '../invoices-page-hooks/use-query-input-param';
import { useFilteredSyncSelectedInvoices } from '../invoices-page-hooks/use-selected-invoices-sync-filter';
import { getBillingStatusLabel } from './InvoiceBillingStatusLabel';
import type { InvoicesColumnsKey } from './InvoiceOverviewTable/invoice-overview-table-consts/invoice-columns';
import { InvoiceOverviewTable } from './InvoiceOverviewTable/InvoiceOverviewTable';
import { InvoicesActionsMenu } from './invoices-actions-menu/InvoicesActionsMenu';
import { InvoicesFilterDrawer } from './InvoicesFilterDrawer';
import { InvoicesPageInfoCards } from './InvoicesPageInfoCards';
import { SyncInvoicesDialog } from './SyncInvoicesDialog/SyncInvoicesDialog';

type InvoicesPageContentProps = {
  invoices: InvoiceOverview[];
  invoicesCount: number;
  excludedColumns?: InvoicesColumnsKey[];
  loadingInvoices?: boolean;
  invoicesOverviewsSummary?: InvoicesOverviewsSummary;
};

export const InvoicesPageContent: React.FC<InvoicesPageContentProps> = ({
  invoices,
  invoicesCount,
  loadingInvoices = false,
  invoicesOverviewsSummary,
}) => {
  const [selectedInvoices, setSelectedInvoices] = useState<InvoiceOverview[]>([]);
  const [addSyncInvoicesDialogOpen, setAddSyncInvoicesDialogOpen] = useState<boolean>(false);
  const [isDownloadingPdf, setDownloadingAsPdf, unsetDownloadingAsPdf] = useActivate(false);
  const [isDownloadingPdfStatement, setDownloadingAsPdfStatement, unsetDownloadingAsPdfStatement] = useActivate(false);
  const [selectedDates, setSelectedDates] = useDatesParam();
  const filteredSelectedInvoices = useFilteredSyncSelectedInvoices(selectedInvoices);
  const [query, setQuery] = useInvoicesPageQuery();
  const { data: connectedIntegrations } = useConnectedIntegrations({ suspense: true });
  const selectedInvoicesIds = useMemo(() => extract(selectedInvoices, 'id'), [selectedInvoices]);
  const apiRef = useGridApiRef();

  validateNotNullOrUndefined(connectedIntegrations, 'connectedIntegrations');

  const updateInvoicesBillingStatusesMutation = useUpdateInvoicesBillingStatuses();
  const syncInvoicesToStripeMutation = useSyncToStripe();
  const syncToNetSuiteMutation = useSyncNetSuiteInvoice();

  const handleSearchInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(event.target.value);
  }, [setQuery]);
  const { isDialogOpen: isFilterDrawerOpen, handleOpen: openFilterDrawer, handleClose: closeFilterDrawer } = useDialog();
  const updateInvoicesReviewStatusesMutation = useUpdateInvoicesReviewStatuses();

  const queryClient = useQueryClient();

  const handleApproveSelectedInvoices = useCallback(async () => {
    validateNotEmptyArray(selectedInvoices, 'selectedInvoices');

    await runMutation(
      updateInvoicesReviewStatusesMutation,
      {
        invoicesByNewStatus: {
          [INVOICE_REVIEW_STATUSES.Approved]: extract(selectedInvoices, 'id'),
        },
      },
      {
        pending: 'Approving invoices...',
        success: 'Invoices approved',
        error: 'Failed to approve invoices',
      },
      {
        onSuccess: async () => {
          await queryClient.resetQueries({
            queryKey: [USE_INVOICES_OVERVIEWS_QUERY_KEY],
          });
        },
      });
  }, [queryClient, selectedInvoices, updateInvoicesReviewStatusesMutation]);

  const handleExportSelectedInvoices = useCallback(async () => {
    validateNotEmptyArray(selectedInvoices, 'selectedInvoices');

    apiRef.current.exportDataAsCsv({
      fileName: 'Invoices',
      allColumns: true,
    });
  }, [apiRef, selectedInvoices]);

  const handleUpdateInvoicesBillingStatus = useCallback(async (billingStatus: InvoiceBillingStatus) => {
    validateNotEmptyArray(selectedInvoices, 'selectedInvoices');
    
    const displayBillingStatus = getBillingStatusLabel(billingStatus);

    await runMutation(updateInvoicesBillingStatusesMutation,
      {
        invoiceStatus: billingStatus,
        invoiceIds: extract(selectedInvoices, 'id'),
      },
      {
        pending: `Updating invoice status to ${displayBillingStatus}...`,
        success: `Invoice status updated to ${displayBillingStatus}`,
        error: `Failed to update invoice status to ${displayBillingStatus}`,
      },
      {
        onSuccess: async () => {
          await queryClient.resetQueries({
            queryKey: [USE_INVOICES_OVERVIEWS_QUERY_KEY],
          });
        },
      });
  }, [queryClient, selectedInvoices, updateInvoicesBillingStatusesMutation]);

  const handleSyncToStripe = useCallback(async () => {
    validateNotEmptyArray(selectedInvoices, 'selectedInvoices');
    
    await runMutation(
      syncInvoicesToStripeMutation,
      { invoiceIds: extract(selectedInvoices, 'id') },
      {
        pending: 'Syncing to Stripe...',
        success: 'Synced to Stripe',
        error: 'Failed to sync to Stripe',
      },
    );
  }, [syncInvoicesToStripeMutation, selectedInvoices]);

  const handleCalendarChange = useCallback((calendarMonths: InvoiceDate[]) => {
    const dates = calendarMonths.map((date) => {
      return {
        month: MONTHS_LABELS.indexOf(date.month).toString(),
        year: date.year,
      };
    } );

    setSelectedDates(dates);
  }, [setSelectedDates]);

  const handleSyncToNetSuite = () => {
    if (isNotNullOrUndefined(filteredSelectedInvoices)) {
      const netSuiteSyncInvoicesIds = filteredSelectedInvoices.map((invoice) => invoice.id);

      syncToNetSuiteMutation.mutate({ invoicesIds: netSuiteSyncInvoicesIds });
    }
  };

  return (
    <>
      <div className="flex h-full flex-col">
        {isDownloadingPdf && (
          <LightInvoicesPdfDownloader
            onFinishedDownloading={() => unsetDownloadingAsPdf()}
            invoicesIds={selectedInvoicesIds ?? []}
          />
        )}
        {isDownloadingPdfStatement && (
          <LightInvoicesPdfDownloader
            onFinishedDownloading={() => unsetDownloadingAsPdfStatement()}
            invoicesIds={selectedInvoicesIds ?? []}
            isStatement
          />
        )}

        <InvoicesPageInfoCards
          invoicesOverviewsSummary={invoicesOverviewsSummary}
        />

        <Paper
          elevation={1}
          sx={{
            height: '100%',
            borderRadius: '12px',
            overflow: 'hidden',
            marginTop: 3,
            position: 'relative',
          }}
        >
          <Stack
            direction="row"
            padding={2}
            spacing={2}
            justifyContent="space-between"
          >
            <Stack
              direction="row"
              spacing={2}
            >
              <TextField
                size="medium"
                label="Search"
                placeholder="Name, email, etc..."
                sx={{
                  width: '300px',
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color={blueGrey[400]}/>
                    </InputAdornment>
                  ),
                }}
                onChange={handleSearchInputChange}
                defaultValue={query ?? ''}
              />

              <CalendarDropdown
                defaultSelectedMonths={selectedDates?.map((date) => ({ month: MONTHS_LABELS[Number(date.month)], year: date.year }) ) ?? []}
                defaultSelectedYear={getCurrentDate().getFullYear()}
                onChange={handleCalendarChange}
              />
            </Stack>

            <Stack
              direction="row"
              alignContent="end"
              spacing={2}
            >
              <div className="flex items-end">
                <Button
                  variant="outlined"
                  size="large"
                  endIcon={<VyIcon icon="Filter"/>}
                  onClick={openFilterDrawer}
                >
                  Filters
                </Button>
              </div>

              <div className="flex items-end">
                <InvoicesActionsMenu
                  invoices={selectedInvoices}
                  onApproveInvoices={handleApproveSelectedInvoices}
                  onExportSelectedInvoices={handleExportSelectedInvoices}
                  onUpdateInvoicesBillingStatus={handleUpdateInvoicesBillingStatus}
                  onDownloadAsPdf={() => setDownloadingAsPdf()}
                  onDownloadAsPdfStatement={() => setDownloadingAsPdfStatement()}
                  onSyncToStripe={() => handleSyncToStripe()}
                  onSyncToNetSuite={() => setAddSyncInvoicesDialogOpen(true)}
                  syncToNetSuiteDisabled={isEmptyArray(filteredSelectedInvoices)}
                  connectedIntegrations={connectedIntegrations}
                  disablePaidMark
                >
                  <Button
                    variant="contained"
                    size="large"
                    endIcon={(
                      <VyIcon
                        icon="SmallArrow"
                        height={24}
                      />
                    )}
                    disabled={isEmptyArray(selectedInvoices)}
                    type="submit"
                  >
                    Actions
                  </Button>
                </InvoicesActionsMenu>
              </div>
            </Stack>
          </Stack>

          <InvoiceOverviewTable
            apiRef={apiRef}
            items={invoices}
            itemsCount={invoicesCount}
            loadingItems={loadingInvoices}
            onInvoicesSelect={(selected: InvoiceOverview[]) => setSelectedInvoices(selected)}
          />
        </Paper>

        <InvoicesFilterDrawer
          open={isFilterDrawerOpen}
          onClose={closeFilterDrawer}
        />

        <SyncInvoicesDialog
          open={addSyncInvoicesDialogOpen}
          onClose={() => setAddSyncInvoicesDialogOpen(false)}
          invoices={filteredSelectedInvoices}
          onSyncInvoices={handleSyncToNetSuite}
        />
      </div>
    </>
  );
};

function useInvoicesPageQuery() {
  const [queryParam, setQueryParam] = useQueryInputParams();
  const [query, setQuery] = useState(queryParam);
  const debouncedQuery = useDebounce(query);

  useQuery({
    queryKey: [debouncedQuery],
    queryFn: () => {
      setQueryParam(debouncedQuery);
    },
  });

  return [debouncedQuery, setQuery] as const;
}